import idb from "idb";
import { action, observable, runInAction, toJS, when } from "mobx";
import { download } from "src/UtilityFunctions/ExportData";
import { MessageStore } from "../Messages/Messages";
import { RootStore } from "../Root/RootStore";
import { UserStore } from "../User/UserStore";


export interface IWellTestSyncError {
    wellId: string;
    wellTestId: string;
    errorMessage: string;
    testDate: number;
}

export interface IWellSyncError {
    wellId: string;
    errorMessage: string;
}

export class SyncStore {
    private MessageStore: MessageStore;
    private UserStore: UserStore;
    private RootStore: RootStore;

    @observable public tryingSync : boolean = false;
    @observable public needSync = false;
    @observable public unsyncedTests: IWellTestSyncError[] = [];
    @observable public offline = false;

    constructor(rootStore: RootStore) {
        // add in listeners.
        this.RootStore = rootStore;
        this.MessageStore = this.RootStore.MessageStore;
        this.UserStore = this.RootStore.UserStore;
        console.log("SyncStore instantiated.");
        when(() => navigator.serviceWorker !== undefined).then(() => {
            navigator.serviceWorker.addEventListener('message', (message) => {
                if( message.data.message === 'needSync') {
                    localStorage.setItem('needSync', 'true');
                    runInAction(()=> { this.needSync = true; });
                } else if( message.data.message === 'removeError') {
                    runInAction(() => {
                        const removeIndex = this.unsyncedTests.findIndex((value) => value.wellTestId === message.data.otherData);
                        if(removeIndex >= 0) {
                            this.unsyncedTests.splice(removeIndex, 1);
                        }
                    });
                }
            });
        });

        this.refreshSyncStore.bind(this);
        this.refreshSyncStore();
    }

    @action
    private refreshSyncStore() {
        this.unsyncedTests = [];
        const db = idb.open('dirty-data-store', 1, (upgradeDb) => {
            this.createDatabase(upgradeDb, false);
        });
        db.then((dbOpened) => {
            dbOpened.transaction('wellTest-error').objectStore('wellTest-error').getAll().then((errors) => {
                if (errors && Array.isArray(errors)) {
                    errors.forEach((value) => {
                        this.unsyncedTests.push(value);
                    });
                }
            });
        });
    }
    @action
    public syncComplete() {
        this.needSync = false;
    }

    @action
    public setOffline(offlineStatus: boolean) {
        if (this.offline !== offlineStatus) {
            this.offline = offlineStatus;
            const messageOffline = "Switching to Offline Processing your data will be saved locally until you return online.";
            const messageOnline = "Returning to Online Processing your data will be saved on the server.";
            this.MessageStore.addMessage(offlineStatus ? messageOffline : messageOnline, 'warning');
            if(this.UserStore.user) {
                this.UserStore.getProfile();
            }
        }
    }
    
    @action
    public setTryingSync(bool: boolean) {
        this.tryingSync = bool;
    }
    @action
    public clearUnsyncedData() {
        clearDirtyData('wellTest-data');
        clearDirtyData('well-data');
        clearDirtyData('wellTest-list');
        clearDirtyData('wellTest-error');
        this.unsyncedTests = [];

    }
    @action
    public async addUnsynedTests(unsyncedTestIds: IWellTestSyncError[]) {
        const db = await idb.open('dirty-data-store');
        const addUnsynced = db.transaction('wellTest-error', 'readwrite');
        unsyncedTestIds.forEach(async (value) => {
            await addUnsynced.objectStore('wellTest-error').put(value, value.wellTestId);
            this.unsyncedTests.push(value);
        }, this);
    }

    public createDatabase(upgradeDb:any , trueIfClean: any) {
        upgradeDb.createObjectStore('wellTest-list');
        upgradeDb.createObjectStore('wellTest-data');
        upgradeDb.createObjectStore('well-list');
        upgradeDb.createObjectStore('well-data');
        if (trueIfClean) {
          upgradeDb.createObjectStore('static-other');
        } else {
          upgradeDb.createObjectStore('wellTest-error');
        }
    }

    public async downloadErrorTests() {
        if (this.unsyncedTests && this.unsyncedTests.length > 0) {
            const db = await idb.open('dirty-data-store');
            const wellTest = await db.transaction('wellTest-data', 'readwrite').objectStore('wellTest-data').getAll();
            if (wellTest && Array.isArray(wellTest)) {
                const jsonData = JSON.stringify(toJS(wellTest.filter(wt => this.unsyncedTests.some(ut => ut.wellTestId === wt.id))), null, '\t');
                download(jsonData, 'SyncErrorTests.json', 'data:text/json;charset=utf-8,');
            }
        }
    }
}

async function clearDirtyData(storeName: any ) {
    const db = await idb.open('dirty-data-store');
    const remTx = db.transaction(storeName, 'readwrite').objectStore(storeName);
    await remTx.clear();
}


// WEBPACK FOOTER //
// ./src/Stores/Sync/SyncStore.ts